// Dependencies
import React from 'react';
import radium from 'radium';
import { connect } from 'react-redux';
import _ from 'lodash';

import LeoForm from '../../../components/form/LeoForm.jsx';
import LeoInput from '../../../components/form/LeoInput.jsx';
import LeoSelect from '../../../components/form/LeoReactSelect';
import countryEnum from '../../../lib/country-enum.js';

import formService from '../../../lib/form-service';

import { updateTicketAddress } from '../../../redux/ticketAddressReducer';
import validate from '../../../lib/email-validation.js';
import { ThemeContext } from "../../../components/theme/Theme.jsx";
import { getCountryCodeFromTheme } from "../../../redux/customerReducer";
import LeoLabel from "../../../components/form/LeoLabel";

const defaultProps = {
	address: {
		countryCode: getCountryCodeFromTheme(),
		salutation: '',
		customSalutation: '',
		firstname: '',
		lastname: '',
		companyName: '',
		additionalInfo: '',
		phoneNumber: '',
		email: '',
		postalCode: '',
		city: '',
		street: '',
		houseNumber: '',
	},
};

class TicketAddressForm extends React.Component {
	static contextType = ThemeContext;

	constructor(props) {
		super(props);

		this.state = {
			eMailInfo: null,
		};
	}

	onEmailChange(e) {
		this.props.onTicketAddressChange(e);
		e.persist();
		if (!this.debounce) {
			this.debounce = _.debounce(() => {
				validate(formService.formatSpecificFieldValues(e.target.name, e.target.value))
					.then((msg) => this.setState({eMailInfo: msg}));
			}, 500);
		}
		this.debounce();
	}

	render() {
		const salutations = {
			MR: 'Herr',
			MS: 'Frau',
			COMPANY: 'Firma',
		};

		const additionalDropDownValuesString = this.context.themeManager.value('ticket-address', 'ticket-address-additional-dropdown_values', null);
		const additionalDropDownValues = additionalDropDownValuesString ? additionalDropDownValuesString.split('|') : null;

		const dropDownDefaultPlaceholder = this.context.themeManager.getThemeTextOrHardcodedDefault('dropDownPlaceholder');
		const additionalDropDownPlaceholder = this.context.themeManager
			.valueOrDefault('ticket-address', 'ticket-address-additional-dropdown_placeholder', dropDownDefaultPlaceholder);

		const functionDropDownValuesString = this.context.themeManager.value('ticket-address', 'ticket-address-function-dropdown_values', null);
		const functionDropDownValues = functionDropDownValuesString ? functionDropDownValuesString.split('|') : null;
		const functionDropDownPlaceholder = this.context.themeManager.valueOrDefault('ticket-address', 'ticket-address-function-dropdown_placeholder',
			dropDownDefaultPlaceholder);

		const placeholders = formService.getPlaceholders(this.context.themeManager, 'ticketAddress');

		const countriesList = Object.entries(countryEnum)
			.sort((a, b) => a[1].localeCompare(b[1]));
		const address = Object.assign({}, this.props.address || defaultProps.address);
		const index = this.props.index;
		return (
			<LeoForm>
				<LeoLabel className={"visually-hidden"} id={"salutation_label_" + index}
						for={`ticket-address_salutation_${index}`} label={placeholders.salutation}/>
				<LeoSelect
					id={`ticket-address_salutation_${index}`}
					name="salutation"
					value={address.salutation}
					valueInput={salutations[address.salutation] ? salutations[address.salutation] : ''}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.salutation}
					options={
						Object.entries(salutations).map(([key, value]) => {
							return {
								value: key,
								label: value,
								isSelected: key === address.salutation,
							}
						})
					}
					isClearable={true}
				>
				</LeoSelect>
				{this.props.showCustomSalutationField && (
					<>
						<LeoLabel className={"visually-hidden"} id={`ticket-address_customSalutation_label_${index}`}
								for={`ticket-address_customSalutation_${index}`} label={placeholders.customSalutation}/>
						<LeoInput
							type="text"
							maxLength="125"
							id={`ticket-address_customSalutation_${index}`}
							name="customSalutation"
							value={address.customSalutation || ''}
							onChange={this.props.onTicketAddressChange}
							placeholder={placeholders.customSalutation}
							errors={formService.findAddressErrorForField(this.props.errors,'customSalutation', placeholders.customSalutation)}
						/>
					</>
				)}
				<LeoLabel className={"visually-hidden"} id={`ticket-address_companyName_label_${index}`}
						for={`ticket-address_companyName_${index}`} label={placeholders.companyName}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_companyName_${index}`}
					name="companyName"
					value={address.companyName}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.companyName}
					autoComplete="organization"
					errors={formService.findAddressErrorForField(this.props.errors,'companyName', placeholders.companyName)}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_firstname_label_${index}`}
						for={`ticket-address_firstname_${index}`} label={placeholders.firstname}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_firstname_${index}`}
					name="firstname"
					value={address.firstname}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.firstname}
					autoComplete="given-name"
					errors={formService.findAddressErrorForField(this.props.errors,'firstname', placeholders.firstname.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_lastname_label_${index}`}
						for={`ticket-address_lastname_${index}`} label={placeholders.lastname}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_lastname_${index}`}
					name="lastname"
					value={address.lastname}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.lastname}
					autoComplete="family-name"
					errors={formService.findAddressErrorForField(this.props.errors,'lastname', placeholders.lastname.replace('*','').trim())}
				/>
				{this.props.showFunctionDropDown &&
					<>
						<LeoLabel className={"visually-hidden"} id={`ticket-address_function_label_${index}`}
								for={`ticket-address_function_${index}`} label={functionDropDownPlaceholder}/>
						<LeoSelect
							id={`ticket-address_function_${index}`}
							name="function"
							value={address.function ? address.function : ''}
							valueInput={address.function ? address.function : ''}
							onChange={this.props.onTicketAddressChange}
							placeholder={functionDropDownPlaceholder}
							errors={formService.findAddressErrorForField(this.props.errors,'function', functionDropDownPlaceholder.replace('*',''))}
							options={
								functionDropDownValues.map((value) => {
									return {
										value: value,
										label: value,
										isSelected: value === address.function,
									}
								})
							}
							isClearable={true}
						>
						</LeoSelect>
					</>
				}
				{this.props.showAdditionalDropDown &&
					<>
					<LeoLabel className={"visually-hidden"} id={`ticket-address_additionalInfo_label_${index}`}
							for={`ticket-address_additionalInfo_${index}`} label={additionalDropDownPlaceholder}/>
					<LeoSelect
						id={`ticket-address_additionalInfo_${index}`}
						name="additionalInfo"
						value={address.additionalInfo ? address.additionalInfo : ''}
						valueInput={address.additionalInfo ? address.additionalInfo : ''}
						onChange={this.props.onTicketAddressChange}
						placeholder={additionalDropDownPlaceholder}
						errors={formService.findAddressErrorForField(this.props.errors,'additionalInfo', additionalDropDownPlaceholder.replace('*',''))}
						options={
							additionalDropDownValues.map((value) => {
								return {
									value: value,
									label: value,
									isSelected: value === address.function,
								}
							})
						}
						isClearable={true}
					>
					</LeoSelect>
					</>
				}
				<LeoLabel className={"visually-hidden"} id={`ticket-address_phoneNumber_label_${index}`}
						for={`ticket-address_phoneNumber_${index}`} label={placeholders.phoneNumber}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_phoneNumber_${index}`}
					name="phoneNumber"
					value={address.phoneNumber}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.phoneNumber}
					autoComplete="phone"
					errors={formService.findAddressErrorForField(this.props.errors,'phoneNumber', placeholders.phoneNumber.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_email_label_${index}`}
						for={`ticket-address_email_${index}`} label={placeholders.email}/>
				<LeoInput
					type="email"
					maxLength="255"
					id={`ticket-address_email_${index}`}
					name="email"
					value={address.email}
					onChange={(e) => this.onEmailChange(e)}
					placeholder={placeholders.email}
					autoComplete="email"
					errors={formService.findAddressErrorForField(this.props.errors,'email', placeholders.email.replace('*','').trim())}
					warn={!!this.state.eMailInfo}
					infoText={this.state.eMailInfo}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_street_label_${index}`}
						for={`ticket-address_street_${index}`} label={placeholders.street}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_street_${index}`}
					name="street"
					value={address.street}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.street}
					autoComplete="new-address-level3"
					errors={formService.findAddressErrorForField(this.props.errors,'street', placeholders.street.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_houseNumber_label_${index}`}
						for={`ticket-address_houseNumber_${index}`} label={placeholders.houseNumber}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_houseNumber_${index}`}
					name="houseNumber"
					value={address.houseNumber}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.houseNumber}
					autoComplete="new-address-level4"
					errors={formService.findAddressErrorForField(this.props.errors,'houseNumber', placeholders.houseNumber.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_postalCode_label_${index}`}
						  for={`ticket-address_postalCode_${index}`} label={placeholders.postalCode}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_postalCode_${index}`}
					name="postalCode"
					value={address.postalCode}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.postalCode}
					autoComplete="postal-code"
					errors={formService.findAddressErrorForField(this.props.errors,'postalCode', placeholders.postalCode.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_city_label_${index}`}
						  for={`ticket-address_city_${index}`} label={placeholders.city}/>
				<LeoInput
					type="text"
					maxLength="255"
					id={`ticket-address_city_${index}`}
					name="city"
					value={address.city}
					onChange={this.props.onTicketAddressChange}
					placeholder={placeholders.city}
					autoComplete="address-level2"
					errors={formService.findAddressErrorForField(this.props.errors,'city', placeholders.city.replace('*','').trim())}
				/>
				<LeoLabel className={"visually-hidden"} id={`ticket-address_countryCode_label_${index}`} for={`ticket-address_countryCode_${index}`} label={placeholders.country}/>
				<LeoSelect
					id={`ticket-address_countryCode_${index}`}
					name="countryCode"
					value={address.countryCode}
					placeholder={placeholders.country}
					valueInput={countryEnum[address.countryCode] ? countryEnum[address.countryCode] : ''}
					onChange={this.props.onTicketAddressChange}
					options={
						countriesList.map(([key, value]) => {
							return {
								value: key,
								label: value,
								isSelected: key === address.countryCode,
							}
						})
					}
				>
				</LeoSelect>
			</LeoForm>
		);
	}
}

const RadiumTicketAddressForm = radium(TicketAddressForm);

const mapStateToProps = (state) => {
	const showAdditionalDropDown = state.ticketAddress.showTicketAddressAdditionalDropDown;
	const showFunctionDropDown = state.ticketAddress.showTicketAddressFunctionDropDown;
	const showCustomSalutationField = state.ticketAddress.showCustomSalutationField;
	return {
		showAdditionalDropDown,
		showFunctionDropDown,
		showCustomSalutationField,
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	onTicketAddressChange: (event) => {
		const value = formService.formatSpecificFieldValues(event.target.name, event.target.value);
		dispatch(updateTicketAddress(event.target.name, value, ownProps.ticketItemUuid));
	},
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(RadiumTicketAddressForm);
